/**
 * All user-facing copy (Albanian). Kept in one typed object so wording can be
 * reviewed by non-developers in a single file and a second language added later.
 *
 * Writing rules (docs/10-ui-guidelines.md#language): plain everyday Albanian, polite "ju",
 * short sentences, say what to do next, no jargon, no em dashes.
 */
export const t = {
  appName: 'Aktivitetet',
  appTagline: 'Platforma e Aktiviteteve',

  common: {
    all: 'Të gjitha',
    none: 'Asnjë',
    save: 'Ruaj',
    cancel: 'Anulo',
    close: 'Mbyll',
    confirm: 'Po, vazhdo',
    keep: 'Jo, kthehu',
    remove: 'Hiq',
    edit: 'Ndrysho',
    delete: 'Fshi',
    back: 'Kthehu',
    search: 'Kërko',
    loading: 'Po ngarkohet…',
    unexpectedError: 'Diçka nuk shkoi. Ju lutemi provoni përsëri pas pak.',
    noResults: 'Nuk u gjet asgjë.',
    yes: 'Po',
    no: 'Jo',
    checkFields: 'Disa fusha duhen plotësuar ose rregulluar. I kemi shënuar me të kuqe.',
    empty: '-',
    perPage: 'Për faqe:',
    rangeOf: (from: number, to: number, count: number) => `${from}-${to} nga ${count}`,
    updated: 'Ndryshuar së fundi',
    when: 'Kur',
  },

  display: {
    largerText: 'Shkronja më të mëdha',
    normalText: 'Shkronja normale',
  },

  nav: {
    dashboard: 'Pasqyra',
    calendar: 'Kalendari',
    activities: 'Aktivitetet',
    users: 'Përdoruesit',
    audit: 'Historiku i veprimeve',
    structure: 'Struktura',
    categories: 'Kategoritë',
    notifications: 'Njoftimet',
    announcements: 'Lajmërimet',
    concerns: 'Shqetësimet',
    messages: 'Mesazhet e javës',
    topics: 'Temat e shqetësimeve',
    toReport: "Për t'u raportuar",
    openMenu: 'Hap menunë',
    main: 'Menuja kryesore',
    collapseSidebar: 'Ngushto menunë anësore',
    expandSidebar: 'Zgjero menunë anësore',
    account: 'Llogaria ime',
  },

  appearance: {
    label: 'Pamja',
    light: 'E çelët',
    dark: 'E errët',
    system: 'Si pajisja',
  },

  settings: {
    menu: 'Cilësimet',
    title: 'Cilësimet',
    subtitle: 'Si shfaqet platforma për ju dhe siguria e llogarisë suaj.',
    sections: 'Pjesët e cilësimeve',
    accessibility: 'Aksesueshmëria',
    security: 'Siguria',
    password: 'Fjalëkalimi',
    devices: 'Pajisjet e lidhura',
    trustedDevices: 'Pajisjet ku nuk kërkohet kodi',
  },

  accessibility: {
    language: 'Gjuha',
    languageHint: 'Gjuha e menyve, butonave dhe mesazheve.',
    albanian: 'Shqip',
    english: 'English',
    englishSoon: 'Anglishtja do të shtohet së shpejti.',
    textSize: 'Madhësia e shkronjave',
    textSizeHint:
      'Me shkronja më të mëdha zmadhohen edhe fushat dhe butonat. Zgjedhja ruhet në këtë pajisje.',
    normal: 'Normale',
    larger: 'Më të mëdha',
    motion: 'Lëvizjet në ekran',
    reduceMotion: 'Pakëso lëvizjet',
    reduceMotionHint:
      'Ndalon animacionet dhe efektet e lëvizjes në ekran. Zgjedhja ruhet në këtë pajisje.',
    appearanceHint:
      'Ngjyra të çelëta ose të errëta. "Si pajisja" ndjek zgjedhjen e telefonit ose kompjuterit tuaj.',
  },

  auth: {
    signInTitle: 'Mirë se vini',
    signInSubtitle: 'Hyni me emailin dhe fjalëkalimin tuaj.',
    email: 'Emaili',
    password: 'Fjalëkalimi',
    signIn: 'Hyni',
    forgotPassword: 'Keni harruar fjalëkalimin?',
    otpTitle: 'Shkruani kodin',
    otpSubtitle: (to: string) =>
      `Ju dërguam një kod me 6 shifra në ${to}. Hapni emailin dhe shkruajeni kodin këtu.`,
    otpSpamHint: 'Nuk e shihni? Shikoni edhe te dosja Spam ose prisni një minutë.',
    otpCode: 'Kodi me 6 shifra',
    verify: 'Vazhdo',
    rememberDevice: (days: number) =>
      `Mos ma kërko kodin në këtë pajisje për ${days} ditë. Zgjidheni vetëm në telefonin ose kompjuterin tuaj.`,
    showPassword: 'Shfaq fjalëkalimin',
    hidePassword: 'Fshih fjalëkalimin',
    trustedDevicesHint:
      'Nëse keni zgjedhur "Mos ma kërko kodin" në një pajisje që nuk e përdorni më, shtypni butonin më poshtë. Kodi do t\'ju kërkohet përsëri kudo.',
    forgetDevices: 'Kërko kodin në çdo pajisje',
    devicesForgotten: "U krye. Herën tjetër kodi do t'ju kërkohet në çdo pajisje.",
    useAnotherAccount: 'Hyni me një llogari tjetër',
    resendCode: 'Më dërgoni kod të ri',
    forgotTitle: 'Keni harruar fjalëkalimin?',
    forgotSubtitle:
      "Shkruani emailin tuaj. Do t'ju dërgojmë një lidhje për të vendosur një fjalëkalim të ri.",
    sendLink: 'Dërgo lidhjen',
    forgotSent:
      "Nëse ky email është i regjistruar, brenda pak minutash do t'ju vijë një mesazh me lidhjen. Shikoni edhe te dosja Spam.",
    backToLogin: 'Kthehu te hyrja',
    resetTitle: 'Vendosni një fjalëkalim të ri',
    inviteTitle: 'Aktivizoni llogarinë tuaj',
    inviteSubtitle: 'Zgjidhni fjalëkalimin me të cilin do të hyni në platformë.',
    newPassword: 'Fjalëkalimi i ri',
    confirmPassword: 'Shkruajeni edhe një herë',
    passwordsDontMatch: 'Dy fjalëkalimet nuk janë njësoj.',
    passwordHint:
      'Të paktën 12 shkronja ose shifra. Një fjali e shkurtër që e mbani mend lehtë është zgjidhje e mirë.',
    resetDone: 'Fjalëkalimi u ruajt. Tani mund të hyni.',
    changePassword: 'Ndrysho fjalëkalimin',
    mustChangePassword:
      'Kjo është hera e parë që hyni. Për sigurinë tuaj, vendosni një fjalëkalim të ri.',
    currentPassword: 'Fjalëkalimi që keni tani',
    passwordChanged: 'Fjalëkalimi u ndryshua.',
    logout: 'Dilni',
  },

  geo: {
    county: 'Qarku',
    municipality: 'Bashkia',
    unit: 'Dega',
    noUnits: 'Kjo bashki nuk ka ende degë',
    location: 'Vendi',
    locationDetail: 'Adresa ose vendi i saktë',
    fixedLocation:
      'Aktivitetet tuaja ruhen automatikisht te dega juaj. Nuk ka nevojë të zgjidhni vendin.',
    inAlbania: 'Në Shqipëri',
    abroad: 'Jashtë vendit',
    where: 'Ku zhvillohet',
    country: 'Shteti',
    city: 'Qyteti',
    countryRequired: 'Zgjidhni shtetin.',
    cityRequired: 'Shkruani qytetin.',
    diasporaHint:
      'Për diasporën shënohen gjithmonë shteti dhe qyteti, që të ndërtohet harta e pranisë sonë jashtë vendit.',
    noCountry: 'Nuk u gjet asnjë shtet me këtë emër.',
  },

  activity: {
    new: 'Shto aktivitet',
    edit: 'Ndrysho aktivitetin',
    title: 'Titulli',
    titlePlaceholder: 'p.sh. Takim me banorët e lagjes Kodra e Diellit',
    titleHint: 'Shkruani konkretisht çfarë ndodh. Lloji i punës e tregon tashmë kategoria.',
    category: 'Kategoria',
    categories: 'Lloji i punës (kategoria)',
    categoriesHint: 'Zgjidhni së pari llojin e punës. Mund të zgjidhni një ose disa.',
    noCategories: 'Nuk ka kategori për ju. Kontaktoni administratorin.',
    message: 'Mesazhi i javës',
    messageHint: 'Nëse aktiviteti përcolli mesazhin politik të javës, zgjidheni këtu.',
    noMessage: 'Asnjë',
    speakers: 'Folës ose të ftuar',
    speakersPlaceholder: 'Shkruani një emër dhe shtypni Enter',
    chipsHint: 'Pas çdo emri shtypni Enter.',
    targetGroup: 'Kujt i drejtohej',
    targetGroupPlaceholder: 'p.sh. të rinjve, grave, pensionistëve',
    budget: 'Buxheti (lekë)',
    outcome: 'Rezultatet',
    outcomePlaceholder: 'Si shkoi, çfarë u arrit dhe çfarë do të bëhet më tej',
    pressLinks: 'Lajme në media',
    pressLinksHint: 'Ngjisni lidhjen e lajmit ose të videos dhe shtypni Enter.',
    pressLinkInvalid:
      'Kjo nuk duket si lidhje interneti. Duhet të fillojë me http:// ose https://.',
    lockedNotice:
      'Ky aktivitet nuk mund të ndryshohet më, sepse ka kaluar afati pas përfundimit. Nëse duhet korrigjuar diçka, kontaktoni administratorin.',
    editableUntil: (when: string) => `Mund ta ndryshoni ose ta fshini deri më ${when}.`,
    details: 'Më shumë detaje',
    lockedShort: 'Nuk ndryshohet më',
    content: 'Përshkrimi i shkurtër',
    contentPlaceholder: 'Tregoni shkurt çfarë do të bëhet: qëllimi, kush merr pjesë…',
    startsAt: 'Fillon',
    endsAt: 'Mbaron',
    participants: 'Sa veta morën pjesë',
    participantsCount: (n: string) => `${n} pjesëmarrës`,
    media: 'Foto, video dhe dokumente',
    status: 'Gjendja',
    stage: 'Gjendja',
    author: 'Hedhur nga',
    taggedYouBy: (name: string) => `${name} ju etiketoi`,
    saveDraft: 'Ruaj si skicë',
    publish: 'Planifiko',
    publishReported: 'Ruaj aktivitetin',
    cancelActivity: 'Anulo aktivitetin',
    cancelConfirmTitle: 'Ta anuloni këtë aktivitet?',
    cancelConfirm:
      'Aktiviteti do të shfaqet si i anuluar. Nuk fshihet, mbetet në histori bashkë me arsyen. Të etiketuarit marrin njoftim.',
    cancelReason: 'Arsyeja e anulimit',
    cancelReasonPlaceholder: 'p.sh. U shty për shkak të motit',
    cancelReasonHint: 'Me pak fjalë. Edhe anulimet janë të dhëna të vlefshme.',
    cancelConfirmButton: 'Po, anuloje',
    cancelled: 'Aktiviteti u anulua.',
    didNotHappen: 'Nuk u zhvillua?',
    cancelledBecause: (reason: string) => `U anulua: ${reason}`,
    deleteConfirmTitle: 'Ta fshini këtë aktivitet?',
    deleteConfirm: 'Aktiviteti do të hiqet nga kalendari dhe nga raportet.',
    deleteConfirmButton: 'Po, fshije',
    saved: 'Aktiviteti u ruajt.',
    published: 'Aktiviteti u planifikua.',
    deleted: 'Aktiviteti u fshi.',
    pastHint:
      'Ky aktivitet ka ndodhur. Plotësoni edhe raportin më poshtë, që të shënohet si i realizuar.',
    futureHint:
      "Ky aktivitet do të bëhet më vonë. Tani mjafton plani. Kur të mbarojë, do t'ju kujtojmë të plotësoni raportin.",
    extraHint: 'Nuk është e detyrueshme. Plotësoni vetëm ato që dini.',
    listTitle: 'Aktivitetet',
    listSubtitle:
      'Të gjitha aktivitetet: të planifikuara, në pritje të raportimit dhe të realizuara.',
    myListSubtitle: 'Aktivitetet që keni planifikuar dhe raportuar.',
    abroadShort: (city: string, country: string) => `${city}, ${country}`,
    allStages: 'Të gjitha',
    calendarTitle: 'Kalendari',
    calendarSubtitle: 'Prekni ose klikoni një ditë për të shtuar një aktivitet.',
    calendarEmpty: 'Nuk ka aktivitete në këtë periudhë.',
    calendarPrev: 'Periudha e mëparshme',
    calendarNext: 'Periudha tjetër',
    calendarToday: 'Shko te sot',
    openActivity: 'Hap aktivitetin',
    empty: 'Nuk ka ende aktivitete.',
    emptyHint: 'Shtypni "Shto aktivitet" për të filluar.',
    selectMunicipality: 'Zgjidhni bashkinë.',
    sections: {
      details: 'Çfarë',
      when: 'Kur',
      where: 'Ku',
      description: 'Përshkrimi',
      extra: 'Më shumë detaje',
      media: 'Foto, video dhe dokumente',
      report: 'Raporti',
    },
  },

  report: {
    title: 'Raporti i aktivitetit',
    subtitle: 'Si shkoi? Plotësojeni sapo të mbarojë, edhe nga telefoni.',
    fill: 'Plotëso raportin',
    edit: 'Ndrysho raportin',
    save: 'Ruaj raportin',
    saved: 'Raporti u ruajt. Aktiviteti u shënua si i realizuar.',
    updated: 'Raporti u përditësua.',
    dueTitle: 'Ky aktivitet pret raportin',
    dueBody:
      'Aktiviteti ka mbaruar. Shënoni sa veta morën pjesë, çfarë u arrit, shqetësimet që dëgjuat dhe shtoni foto ose dokumente.',
    notYet: 'Raporti plotësohet pasi aktiviteti të ketë filluar.',
    participants: 'Sa veta morën pjesë',
    participantsHint: 'Një numër i përafërt mjafton.',
    outcomeRequired: 'Shkruani shkurt si shkoi dhe çfarë u arrit.',
    concerns: 'Shqetësimet e qytetarëve',
    concernsHint:
      'Deri në tri shqetësimet kryesore që dëgjuat. Nga këto partia ndërton pasqyrën e shqetësimeve të vendit, degë pas dege.',
    concernTopic: 'Tema',
    concernText: 'Çfarë thanë, me pak fjalë',
    concernTextPlaceholder: 'p.sh. Uji vjen vetëm dy orë në ditë',
    addConcern: 'Shto një shqetësim',
    removeConcern: 'Hiqe këtë shqetësim',
    concernN: (n: number) => `Shqetësimi ${n}`,
    lockedNotice:
      'Raporti nuk mund të ndryshohet më, sepse ka kaluar afati pas plotësimit. Nëse duhet korrigjuar diçka, kontaktoni administratorin.',
    editableUntil: (when: string) => `Mund ta ndryshoni raportin deri më ${when}.`,
    reportedAt: (when: string) => `Raportuar më ${when}`,
    noReport: 'Ende pa raport.',
    cancelledNoReport: 'Aktiviteti u anulua, prandaj nuk ka raport.',
  },

  stage: {
    awaitingCount: (n: number) =>
      n === 1 ? '1 aktivitet pret raportin tuaj' : `${n} aktivitete presin raportin tuaj`,
    awaitingHint: 'Plotësojini sa më shpejt, që puna juaj të shihet.',
    seeAwaiting: 'Shiko listën',
  },

  groups: {
    label: 'Tipologjia',
    hint: 'Përcakton cilat kategori aktivitetesh sheh. Diaspora shënon shtetin dhe qytetin te çdo aktivitet.',
    none: 'Pa tipologji',
    noneHint: 'Sheh të gjitha kategoritë.',
    all: 'Të gjitha tipologjitë',
    forGroups: 'Për kë është',
    everyone: 'Për të gjithë',
    everyoneHint: 'Nëse nuk zgjidhni asnjë, kategoria shfaqet për të gjithë.',
  },

  concerns: {
    title: 'Shqetësimet e qytetarëve',
    subtitle:
      'Çfarë dëgjojnë përfaqësuesit tanë në terren, sipas temës dhe vendit. Vetëm nga raportet e aktiviteteve të realizuara.',
    total: (n: number) => (n === 1 ? '1 shqetësim' : `${n} shqetësime`),
    byTopic: 'Sipas temës',
    byCounty: 'Sipas qarkut',
    byCountyNote: 'Temat më të shpeshta në çdo qark.',
    list: 'Çfarë thanë',
    empty: 'Nuk ka ende shqetësime të raportuara për këtë periudhë.',
    topic: 'Tema',
    allTopics: 'Të gjitha temat',
    where: 'Ku',
    from: 'Nga aktiviteti',
  },

  topicsAdmin: {
    title: 'Temat e shqetësimeve',
    subtitle:
      'Nga këto zgjedhin përfaqësuesit kur shënojnë shqetësimet e qytetarëve në raport. Lista e mbyllur e bën të mundur përmbledhjen.',
    add: 'Shto temë',
    edit: 'Ndrysho temën',
    name: 'Emri',
    active: 'Aktive (mund të zgjidhet)',
    inactive: 'Joaktive',
    usage: (n: number) => (n === 1 ? '1 shqetësim' : `${n} shqetësime`),
    deleteTitle: 'Ta fshini temën?',
    deleteMessage: (name: string) => `"${name}" do të fshihet dhe nuk mund të rikthehet.`,
    deleteBlocked: 'Nuk mund të fshihet, sepse përdoret në raporte. Mund ta çaktivizoni.',
    saved: 'Tema u ruajt.',
    deleted: 'Tema u fshi.',
  },

  messages: {
    title: 'Mesazhet e javës',
    subtitle:
      'Mesazhi politik që nis nga qendra. Përfaqësuesit e lidhin me aktivitetet e tyre, kështu shihet nëse mesazhi u kthye në veprim.',
    add: 'Shto mesazhin e javës',
    edit: 'Ndrysho mesazhin',
    titleLabel: 'Mesazhi',
    titlePlaceholder: 'p.sh. Kostoja e jetesës',
    summary: 'Shpjegim i shkurtër',
    summaryHint: 'Çfarë duhet të përcjellin strukturat. Nuk është e detyrueshme.',
    startsOn: 'Nga data',
    endsOn: 'Deri më',
    period: (from: string, to: string) => `${from} deri më ${to}`,
    activities: (n: number, r: number) =>
      n === 0
        ? 'Ende pa aktivitete'
        : `${n === 1 ? '1 aktivitet' : `${n} aktivitete`}, ${r} të realizuara`,
    empty: 'Ende nuk është vendosur asnjë mesazh i javës.',
    current: 'Kjo javë',
    saved: 'Mesazhi u ruajt.',
    deleted: 'Mesazhi u fshi.',
    deleteTitle: 'Ta fshini mesazhin?',
    deleteMessage: (title: string) => `"${title}" do të fshihet dhe nuk mund të rikthehet.`,
    deleteBlocked: 'Nuk mund të fshihet, sepse ka aktivitete të lidhura me të. Mund ta ndryshoni.',
    seeActivities: 'Shiko aktivitetet',
  },

  editor: {
    bold: 'Me shkronja të trasha',
    italic: 'Me shkronja të pjerrëta',
    underline: 'I nënvizuar',
    strike: 'I vijëzuar',
    heading: 'Titull',
    bulletList: 'Listë me pika',
    orderedList: 'Listë me numra',
    quote: 'Citim',
    alignLeft: 'Majtas',
    alignCenter: 'Në mes',
    alignRight: 'Djathtas',
    link: 'Lidhje',
    linkAddTitle: 'Shto një lidhje',
    linkEditTitle: 'Ndrysho lidhjen',
    linkUrl: 'Adresa',
    linkHint: 'p.sh. www.pd.al, një email ose një numër telefoni',
    linkText: 'Teksti që do të shfaqet',
    linkTextHint: 'Nëse e lini bosh, shfaqet vetë adresa.',
    linkInvalid:
      'Kjo adresë nuk duket e saktë. Shkruani një faqe interneti, një email ose një telefon.',
    linkRemove: 'Hiq lidhjen',
    undo: 'Zhbëj',
    redo: 'Ribëj',
  },

  media: {
    title: 'Shtoni foto, video ose dokumente',
    dropHere: 'ose tërhiqini këtu nga kompjuteri',
    limits:
      'Foto deri në 15 MB, video deri në 100 MB, dokumente PDF, Word ose Excel deri në 25 MB. Fotot dhe videot ngjeshen automatikisht para ngarkimit, që të ngarkohen shpejt edhe me internet të dobët.',
    choose: 'Zgjidhni skedarët',
    takePhoto: 'Bëni një foto',
    compressing: 'Po ngjishet',
    uploading: 'Po ngarkohet',
    retry: 'Provo përsëri',
    removeFile: 'Hiqe',
    tooLarge: (max: string) => `Skedari është shumë i madh. Madhësia më e madhe është ${max}.`,
    notAccepted:
      'Ky skedar nuk pranohet. Mund të ngarkoni foto, video dhe dokumente PDF, Word ose Excel.',
    uploadFailed: 'Ngarkimi nuk u krye. Kontrolloni internetin dhe provoni përsëri.',
    documents: 'Dokumentet',
    downloadDocument: 'Shkarko dokumentin',
    processing: 'Videoja po përgatitet',
    processingHint: 'Mund ta ruani aktivitetin ndërkohë.',
    failed: 'Kjo video nuk mund të përpunohej. Hiqeni dhe provoni një skedar tjetër.',
    failedShort: 'Nuk u përpunua',
    videoProcessing: 'Videoja po përgatitet dhe do të shfaqet këtu pas pak.',
    videoFailed: 'Kjo video nuk mund të përpunohej.',
    downloadVideo: 'Shkarko videon',
    downloadPhoto: 'Shkarko foton',
  },

  dashboard: {
    title: 'Pasqyra e aktiviteteve',
    subtitle: 'Aktivitetet në të gjithë vendin: çfarë planifikohet dhe çfarë realizohet.',
    total: 'Aktivitete gjithsej',
    planned: 'Të planifikuara',
    awaiting: 'Në pritje të raportimit',
    realised: 'Të realizuara',
    cancelled: 'Të anuluara',
    realisationRate: 'Shkalla e realizimit',
    realisationRateHint:
      'Nga aktivitetet që duhej të ishin zhvilluar deri sot, sa u realizuan me raport. Të anuluarat dhe ato pa raport e ulin.',
    inAdvance: 'Të planifikuara paraprakisht',
    inAdvanceHint: 'Sa aktivitete u vendosën në platformë para se të ndodhnin.',
    delivery: 'Planifikuar dhe realizuar, për person',
    deliveryNote:
      'Kush planifikon shumë dhe realizon pak është informacion po aq i rëndësishëm sa vetë aktivitetet.',
    person: 'Personi',
    rate: 'Realizimi',
    noDelivery: 'Nuk ka ende aktivitete në këtë periudhë.',
    byCountry: 'Jashtë vendit, sipas shtetit',
    byMessage: 'Mesazhet e javës në terren',
    byMessageNote: 'Sa aktivitete përcollën secilin mesazh dhe sa prej tyre u realizuan.',
    group: 'Tipologjia',
    byCounty: 'Sipas qarkut',
    byCategory: 'Sipas kategorisë',
    byCategoryNote: 'Një aktivitet me disa kategori numërohet te secila.',
    upcoming: 'Aktivitetet që vijnë',
    period: 'Periudha',
    periods: {
      month: 'Ky muaj',
      quarter: '3 muajt e fundit',
      year: 'Ky vit',
      all: 'Që nga fillimi',
    },
    nothingUpcoming: 'Nuk ka aktivitete të planifikuara.',
    findPerson: 'Kërko një person',
    noPerson: 'Nuk u gjet asnjë person me këtë emër.',
  },

  users: {
    title: 'Përdoruesit',
    subtitle: 'Llogaritë, rolet dhe degët e secilit.',
    add: 'Shto përdorues',
    edit: 'Ndrysho përdoruesin',
    fullName: 'Emri dhe mbiemri',
    phone: 'Telefoni',
    position: 'Detyra',
    positionPlaceholder: 'p.sh. Deputet i Qarkut Durrës',
    role: 'Roli',
    active: 'Llogaria është aktive',
    inactive: 'Joaktiv',
    lastLogin: 'Hyri së fundi',
    never: 'Ende jo',
    resendInvite: 'Dërgo ftesën përsëri',
    inviteSent: 'Ftesa u dërgua përsëri.',
    created: 'Përdoruesi u shtua. I dërguam një email për të vendosur fjalëkalimin.',
    saved: 'Ndryshimet u ruajtën.',
    deactivate: 'Çaktivizo',
    activate: 'Aktivizo',
    branchRequired: 'Për kryetarin e degës zgjidhni qarkun, bashkinë dhe degën.',
    diasporaNoBranch: 'Diaspora nuk ka degë në Shqipëri: vendi shënohet te çdo aktivitet.',
  },

  structure: {
    title: 'Struktura e partisë',
    subtitle:
      'Qarqet, bashkitë dhe degët. Ndryshimet vlejnë menjëherë për të gjithë. Aktivitetet e kaluara ruajnë vendin ku u regjistruan.',
    counties: 'Qarqet',
    municipalities: 'Bashkitë',
    units: 'Degët',
    selectCounty: 'Zgjidhni një qark',
    selectMunicipality: 'Zgjidhni një bashki',
    emptyUnits: 'Kjo bashki nuk ka ende degë.',
    showInactive: 'Shfaq edhe joaktivët',
    inactive: 'Joaktiv',
    usage: (a: number, u: number) =>
      `${a === 1 ? '1 aktivitet' : `${a} aktivitete`}, ${u} përdorues`,
    countMunicipalities: (n: number) => `${n} bashki`,
    countUnits: (n: number) => `${n} degë`,
    add: {
      counties: 'Shto qark',
      municipalities: 'Shto bashki',
      units: 'Shto degë',
    },
    edit: {
      counties: 'Ndrysho qarkun',
      municipalities: 'Ndrysho bashkinë',
      units: 'Ndrysho degën',
    },
    name: 'Emri',
    code: 'Kodi',
    codeHint: 'Një kod i shkurtër që nuk ndryshon, p.sh. TR-TIR-01. Përdoret kur importoni degët.',
    order: 'Renditja',
    parentCounty: 'Qarku',
    parentMunicipality: 'Bashkia',
    unitType: 'Si quhen ndarjet e kësaj bashkie',
    active: 'Aktiv (shfaqet në listat për zgjedhje)',
    moveWarning:
      'Aktivitetet e kaluara nuk ndryshojnë. Përdoruesit që janë caktuar këtu zhvendosen bashkë me të.',
    deactivateHint:
      'Nuk do të shfaqet më në listat për zgjedhje, por aktivitetet dhe historiku ruhen.',
    deleteTitle: 'Ta fshini përfundimisht?',
    deleteMessage: (name: string) => `"${name}" do të fshihet dhe nuk mund të rikthehet.`,
    deleteBlocked: 'Nuk mund të fshihet, sepse ka aktivitete ose përdorues. Mund ta çaktivizoni.',
    saved: 'Ndryshimet u ruajtën.',
    deleted: 'U fshi.',
    import: {
      button: 'Importo degët (CSV)',
      title: 'Importo degët nga një skedar CSV',
      intro:
        'Skedari ka tre kolona: kodi i bashkisë, kodi i degës dhe emri i degës. Degët që ekzistojnë përditësohen sipas kodit. Asgjë nuk fshihet.',
      template: 'Shkarko shabllonin',
      chooseFile: 'Zgjidhni skedarin CSV',
      orPaste: 'ose ngjisni përmbajtjen këtu',
      check: 'Kontrollo',
      apply: (n: number) => `Importo ${n} degë`,
      preview: (c: number, u: number) => `Do të shtohen ${c} degë dhe do të përditësohen ${u}.`,
      errors: (n: number) =>
        n === 1
          ? 'Ka 1 gabim. Rregullojeni skedarin dhe kontrolloni përsëri.'
          : `Ka ${n} gabime. Rregullojeni skedarin dhe kontrolloni përsëri.`,
      line: 'Rreshti',
      create: 'E re',
      update: 'Përditësim',
      done: (c: number, u: number) => `Importi u krye: ${c} degë të reja, ${u} të përditësuara.`,
    },
  },

  categoriesAdmin: {
    title: 'Kategoritë e aktiviteteve',
    subtitle:
      'Nga këto zgjedhin përdoruesit kur shtojnë një aktivitet. Secili sheh vetëm kategoritë e tipologjisë së vet. Renditja këtu është edhe renditja në formular.',
    add: 'Shto kategori',
    edit: 'Ndrysho kategorinë',
    name: 'Emri',
    color: 'Ngjyra',
    colorHint: 'Ngjyra shfaqet si pikë pranë emrit dhe në kalendar.',
    active: 'Aktive (përdoruesit mund ta zgjedhin)',
    inactive: 'Joaktive',
    usage: (n: number) => (n === 1 ? '1 aktivitet' : `${n} aktivitete`),
    moveUp: 'Ngjite lart',
    moveDown: 'Zbrite poshtë',
    deactivateHint: 'Nuk do të mund të zgjidhet më, por aktivitetet që e kanë e ruajnë.',
    deleteBlocked: 'Nuk mund të fshihet, sepse e përdorin aktivitete. Mund ta çaktivizoni.',
    deleteTitle: 'Ta fshini kategorinë?',
    deleteMessage: (name: string) => `"${name}" do të fshihet dhe nuk mund të rikthehet.`,
    saved: 'Kategoria u ruajt.',
    deleted: 'Kategoria u fshi.',
  },

  audit: {
    title: 'Historiku i veprimeve',
    subtitle: 'Kush hyri, çfarë ndryshoi dhe kur.',
    when: 'Kur',
    who: 'Kush',
    action: 'Çfarë bëri',
    entity: 'Ku',
    ip: 'IP',
    actions: {
      'auth.login': 'Hyri në platformë',
      'auth.login_failed': 'Provoi të hynte, por gaboi',
      'auth.password_reset_requested': 'Kërkoi rivendosjen e fjalëkalimit',
      'auth.login_failed_unknown': 'Hyrja me një email që nuk ekziston u bllokua',
      'auth.login_blocked': 'Provoi të hynte ndërsa llogaria ishte e bllokuar',
      'auth.account_locked': 'Llogaria u bllokua për pak minuta pas shumë përpjekjeve',
      'auth.code_failed': 'Shkroi një kod hyrjeje të gabuar',
      'auth.password_reset': 'Rivendosi fjalëkalimin',
      'auth.password_changed': 'Ndryshoi fjalëkalimin',
      'auth.trusted_devices_revoked': 'Kërkoi kodin në çdo pajisje',
      'auth.session_ended': 'Doli nga një pajisje tjetër',
      'auth.other_sessions_ended': 'Doli nga të gjitha pajisjet e tjera',
      'activity.create': 'Shtoi një aktivitet',
      'activity.update': 'Ndryshoi një aktivitet',
      'activity.delete': 'Fshiu një aktivitet',
      'activity.report': 'Plotësoi raportin e një aktiviteti',
      'activity.report.update': 'Ndryshoi raportin e një aktiviteti',
      'activity.cancel': 'Anuloi një aktivitet',
      'concern-topic.create': 'Shtoi një temë shqetësimesh',
      'concern-topic.update': 'Ndryshoi një temë shqetësimesh',
      'concern-topic.reorder': 'Ndryshoi renditjen e temave',
      'concern-topic.delete': 'Fshiu një temë shqetësimesh',
      'message.create': 'Vendosi mesazhin e javës',
      'message.update': 'Ndryshoi mesazhin e javës',
      'message.delete': 'Fshiu mesazhin e javës',
      'user.create': 'Shtoi një përdorues',
      'user.update': 'Ndryshoi një përdorues',
      'user.invite_resent': 'Dërgoi ftesën përsëri',
      'user.avatar_update': 'Ndryshoi foton e profilit',
      'user.avatar_remove': 'Hoqi foton e profilit',
      'category.create': 'Shtoi një kategori',
      'category.update': 'Ndryshoi një kategori',
      'category.reorder': 'Ndryshoi renditjen e kategorive',
      'category.delete': 'Fshiu një kategori',
      'structure.county_create': 'Shtoi një qark',
      'structure.county_update': 'Ndryshoi një qark',
      'structure.county_delete': 'Fshiu një qark',
      'structure.municipality_create': 'Shtoi një bashki',
      'structure.municipality_update': 'Ndryshoi një bashki',
      'structure.municipality_delete': 'Fshiu një bashki',
      'structure.unit_create': 'Shtoi një degë',
      'structure.unit_update': 'Ndryshoi një degë',
      'structure.unit_delete': 'Fshiu një degë',
      'structure.branch_import': 'Importoi degët',
    } as Record<string, string>,
  },

  people: {
    placeholder: 'Shkruani një emër',
    hint: 'Personat e regjistruar shfaqen me foto dhe etiketohen. Për një të ftuar tjetër, shkruani emrin dhe shtypni Enter.',
    noMatch: 'Nuk ka person të regjistruar me këtë emër. Shtypni Enter për ta shtuar si të ftuar.',
    taggedChip: 'Person i regjistruar',
    guestChip: 'I ftuar',
    pageTitle: 'Profili',
    organised: 'Aktivitete të organizuara',
    delivery: 'Planifikuar dhe realizuar',
    deliveryLine: (realised: number, total: number) =>
      `${realised} të realizuara nga ${total} të planifikuara`,
    taggedIn: 'Aktivitete ku mori pjesë',
    companions: 'Më shpesh bashkë me',
    companionsNote: 'Sa aktivitete të publikuara kanë bashkë, si organizatorë ose të etiketuar.',
    noCompanions: 'Nuk ka ende aktivitete me persona të tjerë.',
    activities: 'Aktivitetet',
    activitiesNote: 'Të gjitha aktivitetet që organizoi ose ku u etiketua.',
    noActivities: 'Nuk ka ende aktivitete.',
    asAuthor: 'Organizoi',
    asTagged: 'Mori pjesë',
    inactive: 'Llogari joaktive',
    together: (n: number) => (n === 1 ? '1 aktivitet bashkë' : `${n} aktivitete bashkë`),
    topPairs: 'Kush punon më shpesh bashkë',
    and: 'dhe',
    topPairsNote: 'Çiftet që shfaqen më shpesh në të njëjtat aktivitete të publikuara.',
    noPairs: 'Ende nuk ka aktivitete me më shumë se një person.',
  },

  sessions: {
    subtitle:
      'Ku është hapur llogaria juaj tani. Nëse nuk e njihni një pajisje, dilni prej saj dhe ndryshoni fjalëkalimin.',
    thisDevice: 'Kjo pajisje',
    openTabs: (n: number) =>
      n === 1 ? 'Hapur vetëm në këtë skedë' : `Hapur në ${n} skeda të këtij shfletuesi`,
    lastUsed: (ago: string) => `Përdorur ${ago}`,
    signedIn: (date: string) => `Hyri më ${date}`,
    ip: (ip: string) => `Adresa IP: ${ip}`,
    unknownBrowser: 'Shfletues i panjohur',
    browserOn: (browser: string, os: string) => `${browser} në ${os}`,
    device: { phone: 'Telefon', tablet: 'Tablet', computer: 'Kompjuter' },
    end: 'Dil nga kjo pajisje',
    endTitle: 'Të dilni nga kjo pajisje?',
    endMessage: (what: string) =>
      `Llogaria do të mbyllet në ${what}. Aty do t'ju duhet fjalëkalimi për të hyrë përsëri.`,
    endConfirm: 'Po, dil',
    ended: 'Dolët nga ajo pajisje.',
    endOthers: 'Dil nga të gjitha pajisjet e tjera',
    endOthersTitle: 'Të dilni nga të gjitha pajisjet e tjera?',
    endOthersMessage: 'Do të mbeteni brenda vetëm në këtë pajisje.',
    othersEnded: 'Dolët nga të gjitha pajisjet e tjera.',
    onlyThisOne: 'Nuk jeni brenda në asnjë pajisje tjetër.',
  },

  profile: {
    menu: 'Profili im',
    title: 'Profili im',
    subtitle: 'Fotoja dhe të dhënat tuaja.',
    photo: 'Fotoja e profilit',
    photoHint:
      'Fotoja shfaqet pranë emrit tuaj te aktivitetet dhe në listat. Ruhet gjithmonë katrore.',
    upload: 'Ngarko një foto',
    change: 'Ndrysho foton',
    remove: 'Hiq foton',
    removeTitle: 'Ta hiqni foton?',
    removeMessage: 'Në vend të fotos do të shfaqen inicialet e emrit tuaj.',
    removeConfirm: 'Po, hiqe',
    cropTitle: 'Rregulloni foton',
    cropHint: 'Tërhiqeni foton që fytyra të jetë në mes të rrethit. Me rrëshqitësin e zmadhoni.',
    zoom: 'Zmadhimi',
    savePhoto: 'Ruaj foton',
    saved: 'Fotoja u ruajt.',
    removed: 'Fotoja u hoq.',
    unreadable: 'Kjo foto nuk mund të hapet. Provoni një foto JPG ose PNG.',
    adminRemove: 'Hiq foton e profilit',
    adminRemoved: 'Fotoja e profilit u hoq.',
  },

  errorPage: {
    title: 'Diçka nuk shkoi',
    body: 'Faqja nuk u hap si duhet. Rifreskojeni. Nëse ndodh përsëri, kontaktoni administratorin.',
    reload: 'Rifresko faqen',
  },

  notifications: {
    title: 'Njoftimet',
    subtitle:
      'Këtu shihni lajmërimet e drejtuesve, kujtesat për raportet e aktiviteteve tuaja, kur dikush ju etiketon në një aktivitet, ose kur ndryshon apo anulohet një aktivitet ku jeni etiketuar.',
    bell: (n: number) =>
      n === 0 ? 'Njoftimet' : `Njoftimet, ${n === 1 ? '1 njoftim i ri' : `${n} njoftime të reja`}`,
    newCount: (n: number) => (n === 1 ? '1 i ri' : `${n} të reja`),
    empty: 'Nuk keni asnjë njoftim.',
    emptyHint: 'Kur dikush ju etiketon në një aktivitet, do ta shihni këtu.',
    markAllRead: 'Shënoji të gjitha si të lexuara',
    seeAll: 'Shiko të gjitha njoftimet',
    showMore: 'Shfaq më shumë',
    unread: 'I ri',
    someone: 'Dikush',
    tagged: (actor: string, title: string) => `${actor} ju etiketoi në aktivitetin "${title}".`,
    changed: (actor: string, title: string) =>
      `${actor} ndryshoi datën, orën ose vendin e aktivitetit "${title}".`,
    cancelled: (actor: string, title: string) => `${actor} anuloi aktivitetin "${title}".`,
    reportDue: (title: string) =>
      `Aktiviteti "${title}" mbaroi. Plotësoni raportin: pjesëmarrja, rezultatet dhe materialet.`,
    announcement: (actor: string, title: string) => `${actor} dërgoi lajmërimin "${title}".`,
    youAreTagged: (author: string) =>
      `${author} ju ka etiketuar në këtë aktivitet. Nuk mund ta ndryshoni, por e shihni këtu dhe në kalendarin tuaj.`,
  },

  announcements: {
    title: 'Lajmërimet',
    subtitle: 'Mesazhe për strukturat e partisë, në platformë dhe me email.',
    new: 'Lajmërim i ri',
    newSubtitle: 'Shkruajeni një herë dhe e marrin të gjithë personat që zgjidhni.',
    empty: 'Ende nuk është dërguar asnjë lajmërim.',
    eyebrow: 'Lajmërim',
    sections: {
      recipients: 'Kush e merr',
      message: 'Mesazhi',
      delivery: 'Si dërgohet',
    },
    roles: 'Rolet',
    rolesHint: 'E marrin vetëm llogaritë aktive me rolet e zgjedhura.',
    rolesRequired: 'Zgjidhni të paktën një rol.',
    county: 'Qarku',
    allCounties: 'Të gjitha qarqet',
    countyHint: 'Vetëm personat që në llogari kanë këtë qark.',
    reach: (n: number) =>
      n === 0
        ? 'Askush nuk e merr me këtë zgjedhje.'
        : n === 1
          ? 'E merr 1 person.'
          : `E marrin ${n} persona.`,
    excludedNote: (n: number) =>
      n === 1 ? '1 person është hequr nga lista.' : `${n} persona janë hequr nga lista.`,
    seeList: 'Shiko listën',
    list: {
      title: 'Kush e merr',
      summary: (count: number, total: number) =>
        total === 1 ? `E merr ${count} nga 1 person.` : `E marrin ${count} nga ${total} persona.`,
      hint: 'Hiqni shenjën te kush nuk duhet ta marrë.',
      search: 'Kërkoni me emër',
      noMatch: 'Askush me këtë emër.',
      selectAll: 'Zgjidh të gjithë',
      selectAllFound: (n: number) => `Zgjidh këta (${n})`,
      unselectAll: 'Hiq të gjithë',
      unselectAllFound: (n: number) => `Hiq këta (${n})`,
      done: 'Mbaroi',
    },
    titleLabel: 'Titulli',
    contentLabel: 'Teksti i lajmërimit',
    contentPlaceholder: 'Shkruani lajmërimin…',
    contentRequired: 'Shkruani tekstin e lajmërimit.',
    inApp: 'Në platformë',
    inAppHint: 'Shfaqet gjithmonë te njoftimet e secilit marrës.',
    email: 'Dërgoje edhe me email',
    emailHint:
      'Çdo marrës merr një email me logon dhe ngjyrat e partisë, në adresën e llogarisë. Emailet dërgohen gradualisht, që të mos shënohen si të padëshiruara, prandaj me shumë marrës mund të zgjasë disa minuta.',
    preview: 'Shiko emailin',
    previewTitle: 'Si duket emaili',
    previewNote: 'Kështu e marrin marrësit, secili me emrin e vet në përshëndetje.',
    send: 'Dërgo lajmërimin',
    confirmTitle: 'Të dërgohet lajmërimi?',
    confirmMessage: (n: number, email: boolean) =>
      `${n === 1 ? 'E merr 1 person' : `E marrin ${n} persona`} në platformë${email ? ' dhe me email' : ''}. Pasi dërgohet, nuk mund të ndryshohet as të tërhiqet.`,
    confirmSend: 'Po, dërgoje',
    sent: 'Lajmërimi u dërgua.',
    everyone: 'Të gjithë',
    rolePlural: {
      SUPER_ADMIN: 'Administratorët',
      LEADERSHIP: 'Drejtuesit',
      MP_EXECUTIVE: 'Deputetët dhe anëtarët e Kryesisë',
      BRANCH_CHAIR: 'Kryetarët e degëve',
    },
    countyOf: (name: string) => `Qarku ${name}`,
    recipients: (n: number) => (n === 1 ? '1 marrës' : `${n} marrës`),
    audience: 'Marrësit',
    sentAt: 'Dërguar',
    author: 'Nga',
    delivery: 'Dërgimi',
    onlyInApp: 'Vetëm në platformë',
    emailQueued: 'Emailet presin radhën pas një lajmërimi tjetër',
    emailSending: (done: number, total: number) => `Emailet po dërgohen: ${done} nga ${total}`,
    emailDone: (sent: number) => (sent === 1 ? '1 email u dërgua' : `${sent} emaile u dërguan`),
    emailFailed: (n: number) => (n === 1 ? '1 nuk u dërgua' : `${n} nuk u dërguan`),
  },

  notFound: {
    title: 'Kjo faqe nuk u gjet',
    back: 'Kthehu në faqen kryesore',
  },
};
